/**
 * EVM Precompile implementations
 */

import { bls12_381 } from "@noble/curves/bls12-381.js";
import { compress as blake2fCompress } from "../../crypto/Blake2/index.js";
import { BN254 as Bn254 } from "../../crypto/bn254/BN254.js";
import { Keccak256 } from "../../crypto/Keccak256/index.js";
import * as Kzg from "../../crypto/KZG/index.js";
import { Ripemd160 } from "../../crypto/Ripemd160/index.js";
import { Secp256k1 } from "../../crypto/Secp256k1/index.js";
import { SHA256 } from "../../crypto/SHA256/index.js";
import * as Gas from "../../primitives/GasConstants/index.js";
import type { HardforkType } from "../../primitives/Hardfork/HardforkType.js";
import * as Hardfork from "../../primitives/Hardfork/index.js";
import type { HashType } from "../../primitives/Hash/index.js";

export enum PrecompileAddress {
	ECRECOVER = "0x0000000000000000000000000000000000000001",
	SHA256 = "0x0000000000000000000000000000000000000002",
	RIPEMD160 = "0x0000000000000000000000000000000000000003",
	IDENTITY = "0x0000000000000000000000000000000000000004",
	MODEXP = "0x0000000000000000000000000000000000000005",
	BN254_ADD = "0x0000000000000000000000000000000000000006",
	BN254_MUL = "0x0000000000000000000000000000000000000007",
	BN254_PAIRING = "0x0000000000000000000000000000000000000008",
	BLAKE2F = "0x0000000000000000000000000000000000000009",
	POINT_EVALUATION = "0x000000000000000000000000000000000000000a",
	BLS12_G1_ADD = "0x000000000000000000000000000000000000000b",
	BLS12_G1_MUL = "0x000000000000000000000000000000000000000c",
	BLS12_G1_MSM = "0x000000000000000000000000000000000000000d",
	BLS12_G2_ADD = "0x000000000000000000000000000000000000000e",
	BLS12_G2_MUL = "0x000000000000000000000000000000000000000f",
	BLS12_G2_MSM = "0x0000000000000000000000000000000000000010",
	BLS12_PAIRING = "0x0000000000000000000000000000000000000011",
	BLS12_MAP_FP_TO_G1 = "0x0000000000000000000000000000000000000012",
	BLS12_MAP_FP2_TO_G2 = "0x0000000000000000000000000000000000000013",
}

export interface PrecompileResult {
	success: boolean;
	output: Uint8Array;
	gasUsed: bigint;
	error?: string;
}

// Utilities
function beBytesToBigInt(bytes: Uint8Array): bigint {
	let result = 0n;
	for (let i = 0; i < bytes.length; i++) {
		result = (result << 8n) | BigInt(bytes[i] ?? 0);
	}
	return result;
}

function bigIntToFixedBytes(value: bigint, size: number): Uint8Array {
	const out = new Uint8Array(size);
	let v = value;
	for (let i = size - 1; i >= 0; i--) {
		out[i] = Number(v & 0xffn);
		v >>= 8n;
	}
	return out;
}

// BLS12-381 Helper Functions

/**
 * Deserialize a G1 point from 128 bytes (EIP-2537 encoding)
 * Format: [x (64 bytes) | y (64 bytes)]
 * Point at infinity: all zeros
 */
function deserializeG1(
	bytes: Uint8Array,
): ReturnType<typeof bls12_381.G1.Point.fromAffine> {
	if (bytes.length !== 128) {
		throw new Error("Invalid G1 point length");
	}

	// Check for point at infinity (all zeros)
	const isZero = bytes.every((b) => b === 0);
	if (isZero) {
		return bls12_381.G1.Point.ZERO;
	}

	// Extract x and y coordinates (64 bytes each, big-endian)
	const x = beBytesToBigInt(bytes.subarray(0, 64));
	const y = beBytesToBigInt(bytes.subarray(64, 128));

	// Construct point from affine coordinates
	const point = bls12_381.G1.Point.fromAffine({ x, y });

	// Validate point is on curve and in correct subgroup
	point.assertValidity();

	return point;
}

/**
 * Serialize a G1 point to 128 bytes (EIP-2537 encoding)
 */
function serializeG1(
	point: ReturnType<typeof bls12_381.G1.Point.fromAffine>,
): Uint8Array {
	const result = new Uint8Array(128);

	// Handle point at infinity
	if (point.equals(bls12_381.G1.Point.ZERO)) {
		return result; // All zeros
	}

	// Convert to affine coordinates
	const affine = point.toAffine();

	// Serialize x and y (64 bytes each, big-endian, left-padded)
	const xBytes = bigIntToFixedBytes(affine.x, 64);
	const yBytes = bigIntToFixedBytes(affine.y, 64);

	result.set(xBytes, 0);
	result.set(yBytes, 64);

	return result;
}

/**
 * Deserialize a G2 point from 256 bytes (EIP-2537 encoding)
 * Format: [x.c0 (64) | x.c1 (64) | y.c0 (64) | y.c1 (64)]
 * Point at infinity: all zeros
 */
function deserializeG2(
	bytes: Uint8Array,
): ReturnType<typeof bls12_381.G2.Point.fromAffine> {
	if (bytes.length !== 256) {
		throw new Error("Invalid G2 point length");
	}

	// Check for point at infinity (all zeros)
	const isZero = bytes.every((b) => b === 0);
	if (isZero) {
		return bls12_381.G2.Point.ZERO;
	}

	// Extract Fp2 coordinates (x = c0 + c1*u, y = c0 + c1*u)
	const xc0 = beBytesToBigInt(bytes.subarray(0, 64));
	const xc1 = beBytesToBigInt(bytes.subarray(64, 128));
	const yc0 = beBytesToBigInt(bytes.subarray(128, 192));
	const yc1 = beBytesToBigInt(bytes.subarray(192, 256));

	// Construct Fp2 elements
	const x = bls12_381.fields.Fp2.create({ c0: xc0, c1: xc1 });
	const y = bls12_381.fields.Fp2.create({ c0: yc0, c1: yc1 });

	// Construct point from affine coordinates
	const point = bls12_381.G2.Point.fromAffine({ x, y });

	// Validate point is on curve and in correct subgroup
	point.assertValidity();

	return point;
}

/**
 * Serialize a G2 point to 256 bytes (EIP-2537 encoding)
 */
function serializeG2(
	point: ReturnType<typeof bls12_381.G2.Point.fromAffine>,
): Uint8Array {
	const result = new Uint8Array(256);

	// Handle point at infinity
	if (point.equals(bls12_381.G2.Point.ZERO)) {
		return result; // All zeros
	}

	// Convert to affine coordinates
	const affine = point.toAffine();

	// Extract Fp2 components (c0, c1)
	const x = affine.x as { c0: bigint; c1: bigint };
	const y = affine.y as { c0: bigint; c1: bigint };

	// Serialize each component (64 bytes, big-endian, left-padded)
	const xc0Bytes = bigIntToFixedBytes(x.c0, 64);
	const xc1Bytes = bigIntToFixedBytes(x.c1, 64);
	const yc0Bytes = bigIntToFixedBytes(y.c0, 64);
	const yc1Bytes = bigIntToFixedBytes(y.c1, 64);

	result.set(xc0Bytes, 0);
	result.set(xc1Bytes, 64);
	result.set(yc0Bytes, 128);
	result.set(yc1Bytes, 192);

	return result;
}

/**
 * Check if an address is a precompile for a given hardfork
 */
// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: precompile checks require branching per hardfork
export function isPrecompile(address: string, hardfork: HardforkType): boolean {
	const normalized = address.toLowerCase();

	// Available in all hardforks
	if (normalized === PrecompileAddress.ECRECOVER.toLowerCase()) return true;
	if (normalized === PrecompileAddress.SHA256.toLowerCase()) return true;
	if (normalized === PrecompileAddress.RIPEMD160.toLowerCase()) return true;
	if (normalized === PrecompileAddress.IDENTITY.toLowerCase()) return true;

	// Available from Byzantium
	if (normalized === PrecompileAddress.MODEXP.toLowerCase()) {
		return Hardfork.isAtLeast(hardfork, Hardfork.BYZANTIUM);
	}
	if (normalized === PrecompileAddress.BN254_ADD.toLowerCase()) {
		return Hardfork.isAtLeast(hardfork, Hardfork.BYZANTIUM);
	}
	if (normalized === PrecompileAddress.BN254_MUL.toLowerCase()) {
		return Hardfork.isAtLeast(hardfork, Hardfork.BYZANTIUM);
	}
	if (normalized === PrecompileAddress.BN254_PAIRING.toLowerCase()) {
		return Hardfork.isAtLeast(hardfork, Hardfork.BYZANTIUM);
	}

	// Available from Istanbul
	if (normalized === PrecompileAddress.BLAKE2F.toLowerCase()) {
		return Hardfork.isAtLeast(hardfork, Hardfork.ISTANBUL);
	}

	// Available from Cancun
	if (normalized === PrecompileAddress.POINT_EVALUATION.toLowerCase()) {
		return Hardfork.isAtLeast(hardfork, Hardfork.CANCUN);
	}

	// BLS precompiles from Prague
	if (normalized === PrecompileAddress.BLS12_G1_ADD.toLowerCase()) {
		return Hardfork.isAtLeast(hardfork, Hardfork.PRAGUE);
	}
	if (normalized === PrecompileAddress.BLS12_G1_MUL.toLowerCase()) {
		return Hardfork.isAtLeast(hardfork, Hardfork.PRAGUE);
	}
	if (normalized === PrecompileAddress.BLS12_G1_MSM.toLowerCase()) {
		return Hardfork.isAtLeast(hardfork, Hardfork.PRAGUE);
	}
	if (normalized === PrecompileAddress.BLS12_G2_ADD.toLowerCase()) {
		return Hardfork.isAtLeast(hardfork, Hardfork.PRAGUE);
	}
	if (normalized === PrecompileAddress.BLS12_G2_MUL.toLowerCase()) {
		return Hardfork.isAtLeast(hardfork, Hardfork.PRAGUE);
	}
	if (normalized === PrecompileAddress.BLS12_G2_MSM.toLowerCase()) {
		return Hardfork.isAtLeast(hardfork, Hardfork.PRAGUE);
	}
	if (normalized === PrecompileAddress.BLS12_PAIRING.toLowerCase()) {
		return Hardfork.isAtLeast(hardfork, Hardfork.PRAGUE);
	}
	if (normalized === PrecompileAddress.BLS12_MAP_FP_TO_G1.toLowerCase()) {
		return Hardfork.isAtLeast(hardfork, Hardfork.PRAGUE);
	}
	if (normalized === PrecompileAddress.BLS12_MAP_FP2_TO_G2.toLowerCase()) {
		return Hardfork.isAtLeast(hardfork, Hardfork.PRAGUE);
	}

	return false;
}

/**
 * Execute a precompile
 * @param address - Precompile address
 * @param input - Input data
 * @param gasLimit - Gas limit for execution
 * @param hardfork - Current hardfork
 * @returns Precompile execution result
 */
// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: precompile execution requires branching per type
export function execute(
	address: string,
	input: Uint8Array,
	gasLimit: bigint,
	_hardfork: HardforkType,
): PrecompileResult {
	const normalized = address.toLowerCase();

	if (normalized === PrecompileAddress.ECRECOVER.toLowerCase()) {
		return ecrecover(input, gasLimit);
	}
	if (normalized === PrecompileAddress.SHA256.toLowerCase()) {
		return sha256(input, gasLimit);
	}
	if (normalized === PrecompileAddress.RIPEMD160.toLowerCase()) {
		return ripemd160(input, gasLimit);
	}
	if (normalized === PrecompileAddress.IDENTITY.toLowerCase()) {
		return identity(input, gasLimit);
	}
	if (normalized === PrecompileAddress.MODEXP.toLowerCase()) {
		return modexp(input, gasLimit);
	}
	if (normalized === PrecompileAddress.BN254_ADD.toLowerCase()) {
		return bn254Add(input, gasLimit);
	}
	if (normalized === PrecompileAddress.BN254_MUL.toLowerCase()) {
		return bn254Mul(input, gasLimit);
	}
	if (normalized === PrecompileAddress.BN254_PAIRING.toLowerCase()) {
		return bn254Pairing(input, gasLimit);
	}
	if (normalized === PrecompileAddress.BLAKE2F.toLowerCase()) {
		return blake2f(input, gasLimit);
	}
	if (normalized === PrecompileAddress.POINT_EVALUATION.toLowerCase()) {
		return pointEvaluation(input, gasLimit);
	}

	// BLS precompiles
	if (normalized === PrecompileAddress.BLS12_G1_ADD.toLowerCase()) {
		return bls12G1Add(input, gasLimit);
	}
	if (normalized === PrecompileAddress.BLS12_G1_MUL.toLowerCase()) {
		return bls12G1Mul(input, gasLimit);
	}
	if (normalized === PrecompileAddress.BLS12_G1_MSM.toLowerCase()) {
		return bls12G1Msm(input, gasLimit);
	}
	if (normalized === PrecompileAddress.BLS12_G2_ADD.toLowerCase()) {
		return bls12G2Add(input, gasLimit);
	}
	if (normalized === PrecompileAddress.BLS12_G2_MUL.toLowerCase()) {
		return bls12G2Mul(input, gasLimit);
	}
	if (normalized === PrecompileAddress.BLS12_G2_MSM.toLowerCase()) {
		return bls12G2Msm(input, gasLimit);
	}
	if (normalized === PrecompileAddress.BLS12_PAIRING.toLowerCase()) {
		return bls12Pairing(input, gasLimit);
	}
	if (normalized === PrecompileAddress.BLS12_MAP_FP_TO_G1.toLowerCase()) {
		return bls12MapFpToG1(input, gasLimit);
	}
	if (normalized === PrecompileAddress.BLS12_MAP_FP2_TO_G2.toLowerCase()) {
		return bls12MapFp2ToG2(input, gasLimit);
	}

	return {
		success: false,
		output: new Uint8Array(0),
		gasUsed: 0n,
		error: `Unknown precompile: ${address}`,
	};
}

/**
 * ECRECOVER precompile (0x01)
 * Recover signer address from signature
 */
export function ecrecover(
	_input: Uint8Array,
	gasLimit: bigint,
): PrecompileResult {
	const gas = 3000n;
	if (gasLimit < gas) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: gas,
			error: "Out of gas",
		};
	}
	// Spec: input padded/truncated to 128 bytes
	const buf = new Uint8Array(128);
	buf.set(_input.slice(0, Math.min(128, _input.length)), 0);

	const hash = buf.slice(0, 32);
	const v = buf[63] ?? 0; // last byte of v (32-byte padded)
	const r = buf.slice(64, 96);
	const s = buf.slice(96, 128);

	try {
		const pub = Secp256k1.recoverPublicKey({ r, s, v }, hash as HashType);
		const hashResult = Keccak256.hash(pub);
		const addr = hashResult.slice(12);
		const out = new Uint8Array(32);
		// left pad 12 zero bytes, then 20-byte address
		out.set(addr, 12);
		return { success: true, output: out, gasUsed: gas };
	} catch {
		// Invalid signature: return zero address (32 bytes all zero)
		return { success: true, output: new Uint8Array(32), gasUsed: gas };
	}
}

/**
 * SHA256 precompile (0x02)
 */
export function sha256(input: Uint8Array, gasLimit: bigint): PrecompileResult {
	const words = BigInt(Math.ceil(input.length / 32));
	const gas = 60n + words * 12n;
	if (gasLimit < gas) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: gas,
			error: "Out of gas",
		};
	}
	const out = SHA256.hash(input);
	return { success: true, output: out, gasUsed: gas };
}

/**
 * RIPEMD160 precompile (0x03)
 */
export function ripemd160(
	input: Uint8Array,
	gasLimit: bigint,
): PrecompileResult {
	const words = BigInt(Math.ceil(input.length / 32));
	const gas = 600n + words * 120n;
	if (gasLimit < gas) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: gas,
			error: "Out of gas",
		};
	}
	const h20 = Ripemd160.hash(input);
	const out = new Uint8Array(32);
	// left pad 12 zero bytes, then 20 bytes hash
	out.set(h20, 12);
	return { success: true, output: out, gasUsed: gas };
}

/**
 * IDENTITY precompile (0x04)
 * Returns input data unchanged
 */
export function identity(
	input: Uint8Array,
	gasLimit: bigint,
): PrecompileResult {
	const gas = 15n + BigInt(Math.ceil(input.length / 32)) * 3n;
	if (gasLimit < gas) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: gas,
			error: "Out of gas",
		};
	}
	return { success: true, output: new Uint8Array(input), gasUsed: gas };
}

/**
 * MODEXP precompile (0x05)
 * Modular exponentiation
 */
export function modexp(_input: Uint8Array, gasLimit: bigint): PrecompileResult {
	if (_input.length < 96) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: 0n,
			error: "Invalid input length",
		};
	}
	const baseLen = beBytesToBigInt(_input.slice(0, 32));
	const expLen = beBytesToBigInt(_input.slice(32, 64));
	const modLen = beBytesToBigInt(_input.slice(64, 96));

	// guard values to JS numbers for slicing
	const bLen = Number(baseLen);
	const eLen = Number(expLen);
	const mLen = Number(modLen);

	const baseStart = 96;
	const expStart = baseStart + bLen;
	const modStart = expStart + eLen;

	const expHead = _input.slice(
		expStart,
		Math.min(expStart + 32, _input.length),
	);
	const expHeadVal = beBytesToBigInt(expHead);
	const gas = Gas.Precompile.calculateModExpCost(
		baseLen,
		expLen,
		modLen,
		expHeadVal,
	);
	if (gasLimit < gas) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: gas,
			error: "Out of gas",
		};
	}

	if (mLen === 0) {
		return { success: true, output: new Uint8Array(0), gasUsed: gas };
	}

	const baseBytes = _input.slice(
		baseStart,
		Math.min(baseStart + bLen, _input.length),
	);
	const expBytes = _input.slice(
		expStart,
		Math.min(expStart + eLen, _input.length),
	);
	const modBytes = _input.slice(
		modStart,
		Math.min(modStart + mLen, _input.length),
	);

	const base = beBytesToBigInt(baseBytes);
	const exp = beBytesToBigInt(expBytes);
	const mod = beBytesToBigInt(modBytes);

	if (mod === 0n) {
		// Division by zero -> empty output per Zig precompile tests
		return { success: true, output: new Uint8Array(0), gasUsed: gas };
	}

	// fast modular exponentiation
	const result = (function powmod(a: bigint, e: bigint, m: bigint): bigint {
		let res = 1n;
		let x = a % m;
		let k = e;
		while (k > 0n) {
			if (k & 1n) res = (res * x) % m;
			x = (x * x) % m;
			k >>= 1n;
		}
		return res % m;
	})(base, exp, mod);

	const out = bigIntToFixedBytes(result, mLen);
	return { success: true, output: out, gasUsed: gas };
}

/**
 * BN254_ADD precompile (0x06)
 * BN254 elliptic curve addition
 */
export function bn254Add(
	_input: Uint8Array,
	gasLimit: bigint,
): PrecompileResult {
	const gas = 150n;
	if (gasLimit < gas) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: gas,
			error: "Out of gas",
		};
	}
	// Input: 128 bytes (G1 point A || G1 point B)
	const buf = new Uint8Array(128);
	buf.set(_input.slice(0, Math.min(128, _input.length)), 0);
	const aBytes = buf.slice(0, 64);
	const bBytes = buf.slice(64, 128);
	try {
		const a = Bn254.deserializeG1(aBytes);
		const b = Bn254.deserializeG1(bBytes);
		const sum = Bn254.G1.add(a, b);
		const out = Bn254.serializeG1(sum);
		return { success: true, output: out, gasUsed: gas };
	} catch (e) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: gas,
			error: String(e),
		};
	}
}

/**
 * BN254_MUL precompile (0x07)
 * BN254 elliptic curve multiplication
 */
export function bn254Mul(
	_input: Uint8Array,
	gasLimit: bigint,
): PrecompileResult {
	const gas = 6000n;
	if (gasLimit < gas) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: gas,
			error: "Out of gas",
		};
	}
	// Input: 96 bytes (G1 point || scalar)
	const buf = new Uint8Array(96);
	buf.set(_input.slice(0, Math.min(96, _input.length)), 0);
	const pBytes = buf.slice(0, 64);
	const sBytes = buf.slice(64, 96);
	try {
		const p = Bn254.deserializeG1(pBytes);
		const s = beBytesToBigInt(sBytes);
		const prod = Bn254.G1.mul(p, s);
		const out = Bn254.serializeG1(prod);
		return { success: true, output: out, gasUsed: gas };
	} catch (e) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: gas,
			error: String(e),
		};
	}
}

/**
 * BN254_PAIRING precompile (0x08)
 * BN254 pairing check
 */
export function bn254Pairing(
	input: Uint8Array,
	gasLimit: bigint,
): PrecompileResult {
	if (input.length % 192 !== 0) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: 0n,
			error: "Invalid input length",
		};
	}
	const k = input.length / 192;
	const gas = 45000n + BigInt(k) * 34000n;
	if (gasLimit < gas) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: gas,
			error: "Out of gas",
		};
	}

	try {
		const pairs: Array<
			[
				ReturnType<typeof Bn254.deserializeG1>,
				ReturnType<typeof Bn254.deserializeG2>,
			]
		> = [];
		for (let i = 0; i < k; i++) {
			const off = i * 192;
			const g1 = Bn254.deserializeG1(input.slice(off, off + 64));
			const g2 = Bn254.deserializeG2(input.slice(off + 64, off + 192));
			pairs.push([g1, g2]);
		}
		const ok = Bn254.Pairing.pairingCheck(pairs);
		const out = new Uint8Array(32);
		if (ok) out[31] = 1;
		return { success: true, output: out, gasUsed: gas };
	} catch (e) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: gas,
			error: String(e),
		};
	}
}

/**
 * BLAKE2F precompile (0x09)
 * Blake2 F compression function per EIP-152
 *
 * Input format (213 bytes):
 * - rounds (4 bytes, big-endian) - number of compression rounds
 * - h (64 bytes) - state vector
 * - m (128 bytes) - message block
 * - t (16 bytes) - offset counters
 * - f (1 byte) - final block flag (must be 0 or 1)
 *
 * @see https://eips.ethereum.org/EIPS/eip-152
 */
export function blake2f(
	_input: Uint8Array,
	gasLimit: bigint,
): PrecompileResult {
	if (_input.length !== 213) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: 0n,
			error: "Invalid input length",
		};
	}

	// Validate final flag per EIP-152 - must be 0 or 1
	const finalFlag = _input[212];
	if (finalFlag !== 0 && finalFlag !== 1) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: 0n,
			error: "Invalid final flag",
		};
	}

	// Parse rounds (big-endian) - use >>> 0 to force unsigned 32-bit interpretation
	const rounds =
		(((_input[0] ?? 0) << 24) |
			((_input[1] ?? 0) << 16) |
			((_input[2] ?? 0) << 8) |
			(_input[3] ?? 0)) >>>
		0;
	const gas = BigInt(rounds);

	if (gasLimit < gas) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: gas,
			error: "Out of gas",
		};
	}

	try {
		const output = blake2fCompress(_input);
		return { success: true, output, gasUsed: gas };
	} catch (e) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: gas,
			error: String(e),
		};
	}
}

/**
 * POINT_EVALUATION precompile (0x0a)
 * KZG point evaluation (EIP-4844)
 *
 * Input format (192 bytes per EIP-4844):
 * - versioned_hash (32 bytes) - hash of the blob commitment
 * - z (32 bytes) - evaluation point
 * - y (32 bytes) - claimed evaluation result
 * - commitment (48 bytes) - KZG commitment
 * - proof (48 bytes) - KZG proof
 */
export function pointEvaluation(
	_input: Uint8Array,
	gasLimit: bigint,
): PrecompileResult {
	const gas = 50000n;
	if (gasLimit < gas) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: gas,
			error: "Out of gas",
		};
	}
	// EIP-4844 specifies exactly 192 bytes
	if (_input.length !== 192) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: gas,
			error: "Invalid input length: expected 192 bytes",
		};
	}
	const versionedHash = _input.slice(0, 32);
	const z = _input.slice(32, 64);
	const y = _input.slice(64, 96);
	const commitment = _input.slice(96, 144);
	const proof = _input.slice(144, 192);

	// Note: Does not validate versioned_hash against sha256(0x01 || commitment)[0:32]
	// Currently only verifies the KZG proof itself
	void versionedHash; // Suppress unused variable warning

	try {
		if (!Kzg.isInitialized()) Kzg.loadTrustedSetup();
		const ok = Kzg.verifyKzgProof(commitment, z, y, proof);
		const out = new Uint8Array(64);
		if (ok) {
			// FIELD_ELEMENTS_PER_BLOB (4096) || BLS_MODULUS (per Zig impl format)
			out[30] = 0x10;
			out[31] = 0x00;
			const blsModulus = Uint8Array.from([
				0x73, 0xed, 0xa7, 0x53, 0x29, 0x9d, 0x7d, 0x48, 0x33, 0x39, 0xd8, 0x08,
				0x09, 0xa1, 0xd8, 0x05, 0x53, 0xbd, 0xa4, 0x02, 0xff, 0xfe, 0x5b, 0xfe,
				0xff, 0xff, 0xff, 0xff, 0x00, 0x00, 0x00, 0x01,
			]);
			out.set(blsModulus, 32);
		}
		return { success: true, output: out, gasUsed: gas };
	} catch (e) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: gas,
			error: String(e),
		};
	}
}

/**
 * BLS12_G1_ADD precompile (0x0b)
 */
export function bls12G1Add(
	input: Uint8Array,
	gasLimit: bigint,
): PrecompileResult {
	const gas = 500n;
	if (gasLimit < gas) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: gas,
			error: "Out of gas",
		};
	}

	try {
		// Validate input length
		if (input.length !== 256) {
			return {
				success: false,
				output: new Uint8Array(0),
				gasUsed: gas,
				error: "Invalid input length",
			};
		}

		// Deserialize G1 points
		const point1 = deserializeG1(input.subarray(0, 128));
		const point2 = deserializeG1(input.subarray(128, 256));

		// Add points
		const result = point1.add(point2);

		// Serialize result
		const output = serializeG1(result);

		return { success: true, output, gasUsed: gas };
	} catch (e) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: gas,
			error: String(e),
		};
	}
}

/**
 * BLS12_G1_MUL precompile (0x0c)
 */
export function bls12G1Mul(
	input: Uint8Array,
	gasLimit: bigint,
): PrecompileResult {
	const gas = 12000n;
	if (gasLimit < gas) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: gas,
			error: "Out of gas",
		};
	}

	try {
		// Validate input length
		if (input.length !== 160) {
			return {
				success: false,
				output: new Uint8Array(0),
				gasUsed: gas,
				error: "Invalid input length",
			};
		}

		// Deserialize G1 point and scalar
		const point = deserializeG1(input.subarray(0, 128));
		const scalar = beBytesToBigInt(input.subarray(128, 160));

		// Handle zero scalar (returns identity point)
		if (scalar === 0n) {
			return { success: true, output: new Uint8Array(128), gasUsed: gas };
		}

		// Multiply point by scalar
		const result = point.multiply(scalar);

		// Serialize result
		const output = serializeG1(result);

		return { success: true, output, gasUsed: gas };
	} catch (e) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: gas,
			error: String(e),
		};
	}
}

/**
 * Calculate MSM gas cost with EIP-2537 discount
 */
function calculateMsmGas(k: number, baseCost: bigint): bigint {
	if (k === 0) return 0n;
	// EIP-2537 discount multipliers
	const multipliers = [
		0, 1, 1.64, 2.1, 2.32, 2.5, 2.64, 2.76, 2.86, 2.94, 3.02, 3.08, 3.14, 3.2,
		3.26, 3.3, 3.34, 3.38, 3.42, 3.46, 3.5, 3.54, 3.58, 3.62, 3.66, 3.7, 3.74,
		3.78, 3.82, 3.86, 3.9, 3.94,
	];
	const multiplier =
		// biome-ignore lint/style/noNonNullAssertion: k < multipliers.length guarantees element exists
		k < multipliers.length ? multipliers[k]! : 3.94 + (k - 31) * 0.04;
	return BigInt(Math.round(Number(baseCost) * multiplier));
}

/**
 * BLS12_G1_MSM precompile (0x0d)
 */
export function bls12G1Msm(
	input: Uint8Array,
	gasLimit: bigint,
): PrecompileResult {
	// Validate input length first
	if (input.length % 160 !== 0 || input.length === 0) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: 0n,
			error: "Invalid input length",
		};
	}

	const k = input.length / 160;
	const gas = calculateMsmGas(k, 12000n);

	if (gasLimit < gas) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: gas,
			error: "Out of gas",
		};
	}

	try {
		const numPairs = Math.floor(input.length / 160);

		// Perform multi-scalar multiplication manually
		let result = bls12_381.G1.Point.ZERO;
		for (let i = 0; i < numPairs; i++) {
			const offset = i * 160;
			const point = deserializeG1(input.subarray(offset, offset + 128));
			const scalar = beBytesToBigInt(
				input.subarray(offset + 128, offset + 160),
			);
			// Skip zero scalar (noble validation)
			if (scalar === 0n) {
				continue;
			}
			result = result.add(point.multiply(scalar));
		}

		// Serialize result
		const output = serializeG1(result);

		return { success: true, output, gasUsed: gas };
	} catch (e) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: gas,
			error: String(e),
		};
	}
}

/**
 * BLS12_G2_ADD precompile (0x0e)
 */
export function bls12G2Add(
	input: Uint8Array,
	gasLimit: bigint,
): PrecompileResult {
	const gas = 800n;
	if (gasLimit < gas) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: gas,
			error: "Out of gas",
		};
	}

	try {
		// Validate input length
		if (input.length !== 512) {
			return {
				success: false,
				output: new Uint8Array(0),
				gasUsed: gas,
				error: "Invalid input length",
			};
		}

		// Deserialize G2 points
		const point1 = deserializeG2(input.subarray(0, 256));
		const point2 = deserializeG2(input.subarray(256, 512));

		// Add points
		const result = point1.add(point2);

		// Serialize result
		const output = serializeG2(result);

		return { success: true, output, gasUsed: gas };
	} catch (e) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: gas,
			error: String(e),
		};
	}
}

/**
 * BLS12_G2_MUL precompile (0x0f)
 */
export function bls12G2Mul(
	input: Uint8Array,
	gasLimit: bigint,
): PrecompileResult {
	const gas = 45000n;
	if (gasLimit < gas) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: gas,
			error: "Out of gas",
		};
	}

	try {
		// Validate input length
		if (input.length !== 288) {
			return {
				success: false,
				output: new Uint8Array(0),
				gasUsed: gas,
				error: "Invalid input length",
			};
		}

		// Deserialize G2 point and scalar
		const point = deserializeG2(input.subarray(0, 256));
		const scalar = beBytesToBigInt(input.subarray(256, 288));

		// Handle zero scalar (returns identity point)
		if (scalar === 0n) {
			return { success: true, output: new Uint8Array(256), gasUsed: gas };
		}

		// Multiply point by scalar
		const result = point.multiply(scalar);

		// Serialize result
		const output = serializeG2(result);

		return { success: true, output, gasUsed: gas };
	} catch (e) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: gas,
			error: String(e),
		};
	}
}

/**
 * BLS12_G2_MSM precompile (0x10)
 */
export function bls12G2Msm(
	input: Uint8Array,
	gasLimit: bigint,
): PrecompileResult {
	// Validate input length first
	if (input.length % 288 !== 0 || input.length === 0) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: 0n,
			error: "Invalid input length",
		};
	}

	const k = input.length / 288;
	const gas = calculateMsmGas(k, 45000n);

	if (gasLimit < gas) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: gas,
			error: "Out of gas",
		};
	}

	try {
		const numPairs = Math.floor(input.length / 288);

		// Perform multi-scalar multiplication manually
		let result = bls12_381.G2.Point.ZERO;
		for (let i = 0; i < numPairs; i++) {
			const offset = i * 288;
			const point = deserializeG2(input.subarray(offset, offset + 256));
			const scalar = beBytesToBigInt(
				input.subarray(offset + 256, offset + 288),
			);
			// Skip zero scalar (noble validation)
			if (scalar === 0n) {
				continue;
			}
			result = result.add(point.multiply(scalar));
		}

		// Serialize result
		const output = serializeG2(result);

		return { success: true, output, gasUsed: gas };
	} catch (e) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: gas,
			error: String(e),
		};
	}
}

/**
 * BLS12_PAIRING precompile (0x11)
 */
export function bls12Pairing(
	input: Uint8Array,
	gasLimit: bigint,
): PrecompileResult {
	// Validate input length first
	if (input.length % 384 !== 0) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: 0n,
			error: "Invalid input length",
		};
	}

	const k = input.length / 384;
	const gas = 65000n + BigInt(k) * 43000n;

	if (gasLimit < gas) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: gas,
			error: "Out of gas",
		};
	}

	try {
		const numPairs = Math.floor(input.length / 384);

		// Handle empty input
		if (numPairs === 0) {
			const output = new Uint8Array(32);
			output[31] = 1; // Empty pairing is valid
			return { success: true, output, gasUsed: gas };
		}

		// Deserialize all G1-G2 pairs
		const pairs: Array<{
			g1: ReturnType<typeof bls12_381.G1.Point.fromAffine>;
			g2: ReturnType<typeof bls12_381.G2.Point.fromAffine>;
		}> = [];
		for (let i = 0; i < numPairs; i++) {
			const offset = i * 384;
			const g1 = deserializeG1(input.subarray(offset, offset + 128));
			const g2 = deserializeG2(input.subarray(offset + 128, offset + 384));
			// Skip pairs with ZERO points (noble validation)
			if (
				!g1.equals(bls12_381.G1.Point.ZERO) &&
				!g2.equals(bls12_381.G2.Point.ZERO)
			) {
				pairs.push({ g1, g2 });
			}
		}

		// Handle case with no valid pairs
		if (pairs.length === 0) {
			const output = new Uint8Array(32);
			output[31] = 1; // Empty pairing is valid
			return { success: true, output, gasUsed: gas };
		}

		// Perform pairing check using pairingBatch
		// This efficiently computes e(G1[0], G2[0]) * e(G1[1], G2[1]) * ... == 1
		const result = bls12_381.pairingBatch(pairs);
		const finalResult = bls12_381.fields.Fp12.finalExponentiate(result);
		const isValid = bls12_381.fields.Fp12.eql(
			finalResult,
			bls12_381.fields.Fp12.ONE,
		);

		// Return 1 if valid, 0 if invalid (as uint256 big-endian)
		const output = new Uint8Array(32);
		output[31] = isValid ? 1 : 0;

		return { success: true, output, gasUsed: gas };
	} catch (e) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: gas,
			error: String(e),
		};
	}
}

/**
 * BLS12_MAP_FP_TO_G1 precompile (0x12)
 */
export function bls12MapFpToG1(
	input: Uint8Array,
	gasLimit: bigint,
): PrecompileResult {
	const gas = 5500n;
	if (gasLimit < gas) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: gas,
			error: "Out of gas",
		};
	}

	try {
		// Validate input length
		if (input.length !== 64) {
			return {
				success: false,
				output: new Uint8Array(0),
				gasUsed: gas,
				error: "Invalid input length",
			};
		}

		// Deserialize Fp element
		const fpElement = beBytesToBigInt(input);

		// Map Fp element to G1 point using hash-to-curve
		const point = bls12_381.G1.hashToCurve(bigIntToFixedBytes(fpElement, 64));

		// Serialize result
		const output = serializeG1(point);

		return { success: true, output, gasUsed: gas };
	} catch (e) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: gas,
			error: String(e),
		};
	}
}

/**
 * BLS12_MAP_FP2_TO_G2 precompile (0x13)
 */
export function bls12MapFp2ToG2(
	input: Uint8Array,
	gasLimit: bigint,
): PrecompileResult {
	const gas = 75000n;
	if (gasLimit < gas) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: gas,
			error: "Out of gas",
		};
	}

	try {
		// Validate input length
		if (input.length !== 128) {
			return {
				success: false,
				output: new Uint8Array(0),
				gasUsed: gas,
				error: "Invalid input length",
			};
		}

		// Map Fp2 element to G2 point using hash-to-curve
		const point = bls12_381.G2.hashToCurve(input);

		// Serialize result
		const output = serializeG2(point);

		return { success: true, output, gasUsed: gas };
	} catch (e) {
		return {
			success: false,
			output: new Uint8Array(0),
			gasUsed: gas,
			error: String(e),
		};
	}
}
